import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import {
  removeAllTokens,
  ROUTES,
  storeAccessToken,
  storeRefreshToken,
  storeUsername,
} from "~/helpers";
import { trackEvent } from "~/utils/analytics";
import {
  CALENDLY_DEMO_URL,
  clearSocialAuthFlags,
  exchangeCodeForTokens,
  LINK_SUCCESS_MESSAGE,
  parseOAuthCallback,
  retryOAuthAfterLinking,
  SOCIAL_AUTH_ERROR_CODES,
} from "~/utils/auth/socialAuth";
import logger from "~/utils/logger";

type CallbackState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "link_success" }
  | { status: "registration_incomplete" };

export const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<CallbackState>({ status: "loading" });

  useEffect(() => {
    const handleCallback = async () => {
      const { code, error, errorDescription } = parseOAuthCallback();

      if (error) {
        if (errorDescription?.includes(LINK_SUCCESS_MESSAGE)) {
          trackEvent({ eventName: "app/auth/social_login.account_linked" });
          removeAllTokens();

          if (retryOAuthAfterLinking()) {
            return;
          }

          setState({ status: "link_success" });
          return;
        }

        logger.error("OAuth callback error:", { error, errorDescription });
        trackEvent({ eventName: "app/auth/social_login.error" });
        setState({
          status: "error",
          message:
            errorDescription || "Authentication failed. Please try again.",
        });
        return;
      }

      if (!code) {
        setState({
          status: "error",
          message: "No authorization code received. Please try again.",
        });
        return;
      }

      try {
        const tokens = await exchangeCodeForTokens(code);

        // Extract username from idToken for token refresh functionality
        // Use proper Base64URL to Base64 conversion for JWT decoding
        const idTokenParts = tokens.idToken.split(".");
        let base64 = idTokenParts[1].replace(/-/g, "+").replace(/_/g, "/");
        while (base64.length % 4 !== 0) base64 += "=";
        const idTokenPayload = JSON.parse(atob(base64)) as Record<
          string,
          unknown
        >;
        const username =
          (idTokenPayload.email as string) ||
          (idTokenPayload["cognito:username"] as string);

        storeAccessToken(tokens.accessToken);
        storeRefreshToken(tokens.refreshToken);
        if (username) {
          storeUsername(username);
        }

        const response = await fetch(
          `${import.meta.env.VITE_BASE_API_URL}/auth/social/register`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${tokens.accessToken}`,
            },
            body: JSON.stringify({}),
          }
        );

        const data = await response.json();

        if (
          response.status === 403 &&
          data.code === SOCIAL_AUTH_ERROR_CODES.PUBLIC_DOMAIN_RESTRICTED
        ) {
          trackEvent({
            eventName: "app/auth/social_login.public_domain_blocked",
          });
          window.location.href = CALENDLY_DEMO_URL;
          return;
        }

        if (
          response.status === 200 &&
          data.status === SOCIAL_AUTH_ERROR_CODES.ALREADY_REGISTERED
        ) {
          clearSocialAuthFlags();
          trackEvent({ eventName: "app/auth/social_login.success" });
          navigate("/");
          return;
        }

        if (
          response.status === 200 &&
          data.status === SOCIAL_AUTH_ERROR_CODES.REGISTRATION_INCOMPLETE
        ) {
          trackEvent({
            eventName: "app/auth/social_login.registration_incomplete",
          });
          setState({ status: "registration_incomplete" });
          navigate("/register/finish");
          return;
        }

        if (response.status === 201) {
          clearSocialAuthFlags();
          trackEvent({
            eventName: "app/auth/social_login.registration_complete",
          });
          navigate("/");
          return;
        }

        throw new Error(data.error || "Registration failed");
      } catch (err) {
        logger.error("Social auth error:", err);
        trackEvent({ eventName: "app/auth/social_login.error" });
        setState({
          status: "error",
          message:
            err instanceof Error
              ? err.message
              : "Authentication failed. Please try again.",
        });
      }
    };

    handleCallback();
  }, [navigate]);

  if (state.status === "loading") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <p className="text-sm text-muted-foreground">
          <FormattedMessage
            id="auth.callback.loading"
            defaultMessage="Completing sign in..."
          />
        </p>
      </div>
    );
  }

  if (state.status === "link_success") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 max-w-md mx-auto text-center">
        <div className="rounded-full bg-green-100 p-3">
          <svg
            className="h-6 w-6 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h2 className="text-xl font-semibold">
          <FormattedMessage
            id="auth.callback.linkSuccess.title"
            defaultMessage="Account Linked"
          />
        </h2>
        <p className="text-sm text-muted-foreground">
          <FormattedMessage
            id="auth.callback.linkSuccess.message"
            defaultMessage='Your account has been linked. Please click "Sign in" one last time to access your account.'
          />
        </p>
        <button
          onClick={() => navigate(ROUTES.LOGIN)}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <FormattedMessage
            id="auth.callback.linkSuccess.button"
            defaultMessage="Go to Sign In"
          />
        </button>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 max-w-md mx-auto text-center">
        <div className="rounded-full bg-red-100 p-3">
          <svg
            className="h-6 w-6 text-red-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>
        <h2 className="text-xl font-semibold">
          <FormattedMessage
            id="auth.callback.error.title"
            defaultMessage="Authentication Failed"
          />
        </h2>
        <p className="text-sm text-muted-foreground">{state.message}</p>
        <button
          onClick={() => navigate(ROUTES.LOGIN)}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <FormattedMessage
            id="auth.callback.error.button"
            defaultMessage="Back to Sign In"
          />
        </button>
      </div>
    );
  }

  return null;
};
